import { Injectable } from '@angular/core';
import { environment } from "../../environments/environment";
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { AuthService } from 'src/core/services/auth.service';
import { AlertService } from "src/core/services/alert.service";
import {MatDialog} from '@angular/material/dialog';
import { BehaviorSubject } from 'rxjs';

import { map, catchError } from "rxjs/operators";
import { throwError } from 'rxjs';

import { SaveConfirmDialogComponent } from 'src/core/components/save-confirm-dialog/save-confirm-dialog.component';
import { Company } from 'src/interfaces/company';
import { PrescriptionCreatePayload } from 'src/interfaces/prescription';

@Injectable({
  providedIn: 'root'
})
export class FormService {
  baseUrl: string = environment.api.baseUrl;
  headers: HttpHeaders;

  constructor(
    private _auth: AuthService,
    private _alertService: AlertService,
    private http: HttpClient,
    private _dialog: MatDialog 
    ) {
    this
      ._auth
      .generateRequestHeaders()
      .then((headers) => {
        this.headers = headers;
      })
  }

  getMedicine(page: number, limit: number) {
    return this
      .http
      .get<any>(
        `${this.baseUrl}/medicine?page=${page}&limit=${limit}`,
        {
          headers: this.headers
        }
      );
  }

  getIndividualTypes() {
    return this
      .http
      .post<any>(`${this.baseUrl}/form-data`
        , ['individual_type'],
        {
          headers: this.headers,
        }
      )
      .pipe(
        map(response => response.data.individual_type),
        catchError((error: HttpErrorResponse) => {
          this._alertService.errorAlert("An error happened loading individual types");
          return throwError(error);
        })
      );

  }

  getDeliveryTypes() {
    return this
      .http
      .post<any>(`${this.baseUrl}/form-data`
        , ["delivery_type"],
        {
          headers: this.headers,
        }
      )
      .pipe(
        map(response => response.data.delivery_type),
        catchError((error: HttpErrorResponse) => {
          this._alertService.errorAlert("An error happened loading delivery methods");
          return throwError(error);
        })
      );
  }

  getCompanyTypes() {
    return this
      .http
      .post<any>(`${this.baseUrl}/form-data`
        , ["company_type"],
        {
          headers: this.headers,
        }
      )
      .pipe(
        map(response => response.data.company_type),
        catchError((error: HttpErrorResponse) => {
          this._alertService.errorAlert("An error happened loading company types");
          return throwError(error);
        })
      );
  }

  getProfileNumber() {

    const lastProfileNumber: string | null = localStorage.getItem('last_profile_number');

    if (lastProfileNumber) {
      const newProfileNumber: string = (parseInt(lastProfileNumber) + 1).toString();
      localStorage.setItem('last_profile_number', newProfileNumber);
      return newProfileNumber;
    }

    localStorage.setItem('last_profile_number', '0');
    return '0';

  }

  getFormData(formData: string[]) {
    return this
      .http
      .post<any>(`${this.baseUrl}/form-data`
        , formData,
        {
          headers: this.headers,
        }
      )
      .pipe(
        map(response => response.data),
        catchError((error: HttpErrorResponse) => {
          this._alertService.errorAlert("An error happened loading form data");
          return throwError(error);
        })
      );
  }

  async getCompanies(page: number, limit: number, search:string=''): Promise<Array<Company>> {
    try{
      const {data} = await this
      .http
      .get<any>(`${this.baseUrl}/company?page=${page}&limit=${limit}&search=${search}`)
      .toPromise();
      return data.results;
    } catch(error){
      const _error: HttpErrorResponse = <HttpErrorResponse>error;
      this._alertService.errorAlert(_error.error.message);
      return [];
    }
  }

  async submitPrescription(payload: PrescriptionCreatePayload): Promise <any> {

    const confirm = await this.confirmSave();
    if(!confirm) return null;

    try{
      const response = await this
      .http
      .post<any>(`${this.baseUrl}/prescription`
        , payload,
        {
          headers: this.headers,
        }
      ).toPromise();

      this._alertService.openSnackBar("Prescription has been successfully created", "close");
      return response;
    } catch(error: any){
      this._alertService.errorAlert("An error happened, could not submit prescription");
      return null;
    }
  }

  confirmSave(): Promise<boolean> {
    const dialogRef = this._dialog.open(SaveConfirmDialogComponent, {
      width: '350px',
    });

    return dialogRef.afterClosed().toPromise();
  }
}